import { Check, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { DRAWER_PHRASING, tenderTone, type TenderPhrasing } from './tenderMath'

/**
 * The one line under the pair: idle until a number is typed, then
 * "Matches" / "Rs X short" / "Rs X over", with "A note will be needed" once
 * the difference crosses the tenant's variance threshold.
 */
export function TenderVerdict({
  delta,
  threshold,
  formatCurrency,
  phrasing = DRAWER_PHRASING,
  idleText,
}: {
  /** Undefined until the operator has typed a number. */
  delta: number | undefined
  threshold: number
  formatCurrency: (n: number) => string
  phrasing?: TenderPhrasing
  idleText: string
}) {
  if (delta === undefined) {
    return (
      <div className="flex min-h-11 items-center gap-2 rounded-lg bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
        <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-muted-foreground/40" aria-hidden="true" />
        {idleText}
      </div>
    )
  }
  const tone = tenderTone(delta, threshold)
  const abs = Math.abs(delta)
  const text =
    tone === 'ok'
      ? 'Matches'
      : `${formatCurrency(abs)} ${delta < 0 ? phrasing.short : phrasing.over}${
          tone === 'bad' ? '. A note will be needed' : ''
        }`
  return (
    <div
      role="status"
      className={cn(
        'flex min-h-11 items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold',
        tone === 'ok' && 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200',
        tone === 'warn' && 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
        tone === 'bad' && 'bg-rose-50 text-rose-800 dark:bg-rose-950/40 dark:text-rose-200',
      )}
    >
      {tone === 'ok' ? (
        <Check className="h-4 w-4 shrink-0" />
      ) : (
        <AlertTriangle className="h-4 w-4 shrink-0" />
      )}
      {text}
    </div>
  )
}
